import React, { useEffect, useState } from "react";
import { fetchMembers, createMember, recordDues, updateMember, remindMember, getCurrency,
  fetchMembershipPlans } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Services & Memberships — members + dues (SM2). Recurring dues are recorded
// manually (telebirr / CBE / cash) and extend the membership expiry. Members are
// registered off a PLAN catalog (SM2b): the plan's areas/dues are snapshot onto
// the membership and the registrant sets the term (start + valid-until).
const S = {
  en: {
    title: "Members", subtitle: "Local spa / gym / pool memberships and dues",
    add: "Add member", name: "Member name", phone: "Phone", plan: "Plan name",
    areas: "Areas (e.g. gym,pool or all)", dues: "Dues amount", cycle: "Billing cycle",
    monthly: "Monthly", quarterly: "Quarterly", annual: "Annual", save: "Save member",
    none: "No members yet — add your first above.", active: "Active", expired: "Expired", frozen: "Frozen",
    expires: "Expires", rec_dues: "Record dues", freeze: "Freeze", unfreeze: "Reactivate",
    method: "Payment", m_cash: "Cash", m_telebirr: "telebirr", m_cbe: "CBE Birr", m_pos: "POS",
    confirm: "Confirm", cancel: "Cancel", paid: "Dues recorded — expiry extended.",
    overdue: "Overdue", due_in: "Due in", arrears_l: "owed", behind_n: "behind",
    remind: "Remind", reminded: "Reminded",
    f_all: "All", f_overdue: "Overdue", f_due: "Due soon", f_active: "Active", f_frozen: "Frozen",
    plans_title: "Membership plans", manage_plans: "Manage plans", add_plan: "Add plan",
    save_plan: "Save plan", edit: "Edit", del: "Delete", inactive_l: "Inactive",
    select_plan: "Select a plan", no_plans: "No plans yet — add one to start.",
    need_plan: "Add a plan first to register members.",
    start_date: "Start date", valid_until: "Valid until", active_l: "Active", private_client: "Private / VIP client", vip_note: "VIP note (optional)",
    a_spa: "Spa", a_gym: "Gym", a_pool: "Pool", a_salon: "Salon", a_activity: "Activity",
  },
  am: {
    title: "አባላት", subtitle: "የአካባቢ ስፓ / ጂም / መዋኛ አባልነትና ክፍያ",
    add: "አባል ጨምር", name: "የአባል ስም", phone: "ስልክ", plan: "የፕላን ስም",
    areas: "ዘርፎች (ለምሳሌ gym,pool ወይም all)", dues: "የክፍያ መጠን", cycle: "የክፍያ ዑደት",
    monthly: "ወርሃዊ", quarterly: "ሩብ ዓመት", annual: "ዓመታዊ", save: "አባል አስቀምጥ",
    none: "እስካሁን አባል የለም — ከላይ ይጨምሩ።", active: "ገቢር", expired: "ጊዜው አልፎበታል", frozen: "የቆመ",
    expires: "የሚያበቃው", rec_dues: "ክፍያ መዝግብ", freeze: "አቁም", unfreeze: "እንደገና አግብር",
    method: "ክፍያ", m_cash: "ጥሬ ገንዘብ", m_telebirr: "ቴሌብር", m_cbe: "ሲቢኢ ብር", m_pos: "ፖስ",
    confirm: "አረጋግጥ", cancel: "ሰርዝ", paid: "ክፍያ ተመዝግቧል — ጊዜው ተራዝሟል።",
    overdue: "ያለፈበት", due_in: "ይቀራል", arrears_l: "ዕዳ", behind_n: "ዘግይተዋል",
    remind: "አስታውስ", reminded: "ተታውሷል",
    f_all: "ሁሉም", f_overdue: "ያለፈበት", f_due: "በቅርቡ የሚያበቃ", f_active: "ገቢር", f_frozen: "የቆመ",
    plans_title: "የአባልነት ፕላኖች", manage_plans: "ፕላኖችን አስተዳድር", add_plan: "ፕላን ጨምር",
    save_plan: "ፕላን አስቀምጥ", edit: "አርትዕ", del: "ሰርዝ", inactive_l: "ያልነቃ",
    select_plan: "ፕላን ይምረጡ", no_plans: "እስካሁን ፕላን የለም — አንድ ይጨምሩ።",
    need_plan: "አባላትን ለመመዝገብ መጀመሪያ ፕላን ይጨምሩ።",
    start_date: "የመጀመሪያ ቀን", valid_until: "የሚያገለግለው እስከ", active_l: "ገቢር", private_client: "የግል / VIP ደንበኛ", vip_note: "የVIP ማስታወሻ (አማራጭ)",
    a_spa: "ስፓ", a_gym: "ጂም", a_pool: "መዋኛ", a_salon: "ሳሎን", a_activity: "እንቅስቃሴ",
  },
  om: {
    title: "Miseensota", subtitle: "Miseensummaa spaa / jiimii / daakaa naannoo fi kaffaltii",
    add: "Miseensa dabali", name: "Maqaa miseensaa", phone: "Bilbila", plan: "Maqaa karooraa",
    areas: "Naannoolee (fkn gym,pool yookaan all)", dues: "Hamma kaffaltii", cycle: "Marsaa kaffaltii",
    monthly: "Ji'aan", quarterly: "Kurmaana", annual: "Waggaan", save: "Miseensa olkaa'i",
    none: "Hanga ammaa miseensi hin jiru — olitti dabali.", active: "Kan hojjetu", expired: "Yeroon darbe", frozen: "Kan dhaabbate",
    expires: "Kan xumuramu", rec_dues: "Kaffaltii galmeessi", freeze: "Dhaabi", unfreeze: "Deebisii kaasi",
    method: "Kaffaltii", m_cash: "Maallaqa", m_telebirr: "telebirr", m_cbe: "CBE Birr", m_pos: "POS",
    confirm: "Mirkaneessi", cancel: "Haqi", paid: "Kaffaltiin galmaa'e — yeroon dheerate.",
    overdue: "Yeroon darbe", due_in: "Guyyaa hafe", arrears_l: "idaa", behind_n: "duubatti",
    remind: "Yaadachiisi", reminded: "Yaadachiifame",
    f_all: "Hunda", f_overdue: "Yeroon darbe", f_due: "Dhiyootti xumuramu", f_active: "Kan hojjetu", f_frozen: "Kan dhaabbate",
    plans_title: "Karoora miseensummaa", manage_plans: "Karoora bulchi", add_plan: "Karoora dabali",
    save_plan: "Karoora olkaa'i", edit: "Gulaali", del: "Haqi", inactive_l: "Kan hin hojjenne",
    select_plan: "Karoora fili", no_plans: "Hanga ammaa karoorri hin jiru — tokko dabali.",
    need_plan: "Miseensa galmeessuuf duraan karoora dabali.",
    start_date: "Guyyaa jalqabaa", valid_until: "Hanga kan tajaajilu", active_l: "Kan hojjetu", private_client: "Maamila dhuunfaa / VIP", vip_note: "Yaadannoo VIP (filannoo)",
    a_spa: "Spaa", a_gym: "Jiimii", a_pool: "Daakaa", a_salon: "Saaloonii", a_activity: "Sochii",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const inp = { padding: "9px 10px", border: "1px solid #d8dee2", borderRadius: 8, fontSize: 14 };
const inpRO = { ...inp, background: "#f6f8f8", color: "#5b6472" };
const CYCLE_MONTHS = { monthly: 1, quarterly: 3, annual: 12 };

const today = () => new Date().toISOString().slice(0, 10);

// Mirror of the backend _add_cycle so the "valid until" auto-fills to the same
// paid-through date the server would compute.
const addCycle = (iso, cycle) => {
  const base = new Date((iso || today()) + "T00:00:00");
  if (isNaN(base)) return "";
  const day = base.getDate();
  const d = new Date(base.getFullYear(), base.getMonth() + (CYCLE_MONTHS[cycle] || 1), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${dd}`;
};

const EMPTY = { customer_name: "", customer_phone: "", plan_id: "", areas: "", dues_amount: "", dues_cycle: "monthly", start_date: today(), end_date: addCycle(today(), "monthly"), private: false, vip_note: "" };

export default function MembersPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [duesFor, setDuesFor] = useState(null);
  const [method, setMethod] = useState("cash");
  const [flash, setFlash] = useState("");
  const [filter, setFilter] = useState("all");
  const [reminding, setReminding] = useState(null);
  const [remindInfo, setRemindInfo] = useState(null);   // { id, phone } of the just-reminded member
  // SM2b — plan catalog (read-only here; plans are authored on the Membership
  // plans catalog page). Registration snapshots the picked plan's price/areas.
  const [plans, setPlans] = useState(null);

  const load = () => fetchMembers().then(setRows).catch(() => setRows([]));
  const loadPlans = () => fetchMembershipPlans().then(setPlans).catch(() => setPlans([]));
  useEffect(() => { load(); loadPlans(); }, []);

  const activePlans = (plans || []).filter((p) => p.active);

  const onSelectPlan = (id) => {
    const p = (plans || []).find((x) => String(x.id) === String(id));
    if (!p) { setForm({ ...form, plan_id: "", areas: "", dues_amount: "", dues_cycle: "monthly", end_date: addCycle(form.start_date, "monthly") }); return; }
    setForm({ ...form, plan_id: p.id, areas: (p.areas || []).join(","),
      dues_amount: p.dues_amount, dues_cycle: p.dues_cycle,
      end_date: addCycle(form.start_date || today(), p.dues_cycle) });
  };
  const onStartChange = (v) => setForm({ ...form, start_date: v, end_date: addCycle(v, form.dues_cycle) });

  const save = () => {
    if (!form.customer_name.trim() || !form.plan_id) return;
    setBusy(true);
    createMember({
      customer_name: form.customer_name.trim(), customer_phone: form.customer_phone.trim() || null,
      plan_id: Number(form.plan_id),
      start_date: form.start_date || null, end_date: form.end_date || null,
      private: !!form.private, vip_note: form.vip_note.trim() || null,
    }).then(() => { setForm({ ...EMPTY, start_date: today(), end_date: addCycle(today(), "monthly") }); load(); }).catch(() => {}).finally(() => setBusy(false));
  };
  const doDues = (m) => {
    recordDues(m.id, { method }).then(() => { setDuesFor(null); setFlash(t("paid")); load(); }).catch(() => {});
  };
  const toggleFreeze = (m) => updateMember(m.id, { status: m.status === "frozen" ? "active" : "frozen" }).then(load).catch(load);
  const doRemind = (m) => {
    setReminding(m.id);
    remindMember(m.id).then((res) => { setRemindInfo({ id: m.id, phone: res.phone }); load(); })
      .catch(() => {}).finally(() => setReminding(null));
  };

  const badge = (m) => {
    const st = m.pay_state || (m.status === "frozen" ? "frozen" : m.expired ? "overdue" : "current");
    if (st === "frozen") return { label: t("frozen"), bg: "#EEF0F2", fg: "#5b6472" };
    if (st === "overdue") return { label: `${t("overdue")} ${m.days_overdue}d`, bg: "#FDECEC", fg: "#A32D2D" };
    if (st === "due_soon") return { label: `${t("due_in")} ${m.due_in_days}d`, bg: "#FFF4E5", fg: "#9A6400" };
    return { label: t("active"), bg: "#EAF6EE", fg: "#1D7A44" };
  };

  const rowsArr = rows || [];
  const counts = { all: rowsArr.length, overdue: 0, due_soon: 0, current: 0, frozen: 0 };
  rowsArr.forEach((m) => { counts[m.pay_state] = (counts[m.pay_state] || 0) + 1; });
  const chips = [
    { key: "all", label: t("f_all"), n: counts.all },
    { key: "overdue", label: t("f_overdue"), n: counts.overdue },
    { key: "due_soon", label: t("f_due"), n: counts.due_soon },
    { key: "current", label: t("f_active"), n: counts.current },
    { key: "frozen", label: t("f_frozen"), n: counts.frozen },
  ];
  const shown = filter === "all" ? rowsArr : rowsArr.filter((m) => m.pay_state === filter);

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      {/* Add member — plan-driven */}
      <div style={{ ...CARD, padding: 16, marginTop: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>{t("add")}</div>
        {activePlans.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>{t("need_plan")}</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
              <input style={inp} placeholder={t("name")} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              <input style={inp} placeholder={t("phone")} value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
              <select style={inp} value={form.plan_id} onChange={(e) => onSelectPlan(e.target.value)}>
                <option value="">{t("select_plan")}</option>
                {activePlans.map((p) => <option key={p.id} value={p.id}>{p.name} · {formatPrice(p.dues_amount, cur)}/{t(p.dues_cycle).toLowerCase()}</option>)}
              </select>
              <input style={inpRO} placeholder={t("areas")} value={form.areas} readOnly />
              <input style={inpRO} type="number" placeholder={t("dues")} value={form.dues_amount} readOnly />
              <input style={inpRO} placeholder={t("cycle")} value={form.dues_cycle ? t(form.dues_cycle) : ""} readOnly />
              <label style={{ fontSize: 12, color: "#5b6472" }}>{t("start_date")}
                <input style={{ ...inp, width: "100%", marginTop: 3 }} type="date" value={form.start_date} onChange={(e) => onStartChange(e.target.value)} />
              </label>
              <label style={{ fontSize: 12, color: "#5b6472" }}>{t("valid_until")}
                <input style={{ ...inp, width: "100%", marginTop: 3 }} type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </label>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 13, color: "#5b6472", display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={!!form.private} onChange={(e) => setForm({ ...form, private: e.target.checked })} />
                🔒 {t("private_client")}
              </label>
              {form.private && (
                <input style={{ ...inp, width: "100%", marginTop: 6 }} placeholder={t("vip_note")} value={form.vip_note} onChange={(e) => setForm({ ...form, vip_note: e.target.value })} />
              )}
            </div>
            <div style={{ marginTop: 12 }}>
              <button className="btn primary small" disabled={busy || !form.customer_name.trim() || !form.plan_id} onClick={save}>{t("save")}</button>
            </div>
          </>
        )}
      </div>

      {flash && <div style={{ fontSize: 13, color: "#0F6E56", marginTop: 10, fontWeight: 600 }}>{flash}</div>}

      {rowsArr.length > 0 && (
        <>
          {counts.overdue > 0 && (
            <div style={{ ...CARD, background: "#FDECEC", border: "1px solid #F6C9C9", color: "#A32D2D", padding: "10px 14px", marginTop: 12, fontWeight: 700 }}>
              {counts.overdue} {t("behind_n")}
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
            {chips.map((c) => (
              <button key={c.key} onClick={() => setFilter(c.key)}
                style={{ border: "1px solid " + (filter === c.key ? "#0F6E56" : "#d8dee2"),
                  background: filter === c.key ? "#0F6E56" : "#fff", color: filter === c.key ? "#fff" : "#3a444d",
                  borderRadius: 16, padding: "5px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                {c.label} ({c.n})
              </button>
            ))}
          </div>
        </>
      )}

      {rows == null ? <p className="muted" style={{ marginTop: 16 }}>…</p> : rows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 12, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {shown.map((m) => {
            const b = badge(m);
            return (
              <div key={m.id} style={{ ...CARD, padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ background: b.bg, color: b.fg, borderRadius: 16, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>{b.label}</span>
                  {m.arrears > 0 && <span style={{ color: "#A32D2D", fontSize: 12.5, fontWeight: 700 }}>{formatPrice(m.arrears, cur)} {t("arrears_l")}</span>}
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <div style={{ fontWeight: 700 }}>{m.private ? "🔒 " : ""}{m.name} <span className="muted" style={{ fontWeight: 400, fontSize: 12.5 }}>· {m.plan_name}</span></div>
                    <div className="muted" style={{ fontSize: 12.5 }}>{m.areas || "all"} · {t("expires")}: {m.end_date} · {formatPrice(m.dues_amount, cur)}/{t(m.dues_cycle).toLowerCase()}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {(m.pay_state === "overdue" || m.pay_state === "due_soon") && (
                      <button className="btn secondary small" disabled={reminding === m.id} onClick={() => doRemind(m)}>{t("remind")}</button>
                    )}
                    <button className="btn primary small" onClick={() => { setDuesFor(duesFor === m.id ? null : m.id); setMethod("cash"); }}>{t("rec_dues")}</button>
                    <button className="btn secondary small" onClick={() => toggleFreeze(m)}>{m.status === "frozen" ? t("unfreeze") : t("freeze")}</button>
                  </div>
                </div>
                {(m.last_reminded_at || (remindInfo && remindInfo.id === m.id)) && (
                  <div style={{ marginTop: 8, fontSize: 12.5, color: "#3a444d", display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {(remindInfo && remindInfo.id === m.id ? remindInfo.phone : m.phone) &&
                      <span style={{ fontWeight: 700 }}>{t("phone")}: {(remindInfo && remindInfo.id === m.id ? remindInfo.phone : m.phone)}</span>}
                    {m.last_reminded_at && <span className="muted">{t("reminded")}: {String(m.last_reminded_at).slice(0, 10)}</span>}
                  </div>
                )}
                {duesFor === m.id && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                    <span style={{ fontWeight: 700, color: "#27500A" }}>{formatPrice(m.dues_amount, cur)}</span>
                    <select style={inp} value={method} onChange={(e) => setMethod(e.target.value)}>
                      <option value="cash">{t("m_cash")}</option>
                      <option value="telebirr">{t("m_telebirr")}</option>
                      <option value="cbe_birr">{t("m_cbe")}</option>
                      <option value="pos">{t("m_pos")}</option>
                    </select>
                    <button className="btn primary small" onClick={() => doDues(m)}>{t("confirm")}</button>
                    <button className="btn secondary small" onClick={() => setDuesFor(null)}>{t("cancel")}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
